import {computed, defineComponent, onMounted, reactive} from '@vue/composition-api';
import {ConnectionStatus} from "@/views/connections/connections-types";
import {getServiceConnectionsMap, ServiceInfo, ServiceName, serviceNames, JobylonService, defaultAmsService, defaultJobylonService, TeamtailorService, defaultTeamtailorService, defaultTeamtailorXmlService, TeamtailorXmlService, defaultPontyService, PontyService, IntensoService, defaultIntensoService, TeamtailorApiServiceInfo, defaultTeamtailorApiService} from "@/functions/services-functions";
import axios from "axios";
import {SettingsContext} from "@/utilities/settings-context";
import {createPageContext} from "@/components/navbar-box/page-context";
import {generalError} from "@/functions/system-modal-functions";
import {i18n} from "@/utilities/vue-i18n";
import JobylonConnection from "@/components/connection-jobylon/connection-jobylon.vue";
import TeamtailorConnection from "@/components/connection-teamtailor/connection-teamtailor.vue";
import TeamtailorXmlConnection from "@/components/connection-teamtailor-xml/connection-teamtailor-xml.vue";
import PontyConnection from "@/components/connection-ponty/connection-ponty.vue";
import IntensoConnection from "@/components/connection-intenso/connection-intenso.vue";
import TeamtailorApiConnection from "@/components/connection-teamtailor-api/connection-teamtailor-api.vue";
import { useFeatureFlags } from '@/states/feature-flags-state';

type SettingsType = {
    connections: Partial<Record<ServiceName, ServiceInfo>>;
    loaded: boolean;
};

export default defineComponent({
    components: {
        JobylonConnection,
        TeamtailorConnection,
        TeamtailorXmlConnection,
    PontyConnection,
    IntensoConnection,
    TeamtailorApiConnection,
    },
    props: {
        companyId: {
            required: true,
            type: Number,
        },
    },
    setup(props, context){
        const featureFlags = useFeatureFlags();

        const teamTailorEnabled = computed(() => featureFlags.userTeamtailorServiceConnection);

        const teamTailorXmlEnabled = computed(() => featureFlags.userTeamtailorXmlServiceConnection);

    const pontyEnabled = computed(() => featureFlags.pontyServiceConnection);

        const settings = reactive<SettingsType>({
            connections: {
                [serviceNames.ams]: defaultAmsService(),
                [serviceNames.jobylon]: defaultJobylonService(),
                [serviceNames.teamtailor]: defaultTeamtailorService(),
                [serviceNames.teamtailorXml]: defaultTeamtailorXmlService(),
                [serviceNames.teamtailorApi]: defaultTeamtailorApiService(),
                [serviceNames.ponty]: defaultPontyService(),
                [serviceNames.intenso]: defaultIntensoService(),
            },
            loaded: false,
        });

        const shouldDisableTeamtailor = computed(() => settings.connections[serviceNames.teamtailorXml]?.active === true);

        const model = reactive({ams:{username: '', password: ''}});

        const states = reactive({amsError: null as unknown as string});

        const fetchList = async () => {

            settings.loaded = false;

            const map = await getServiceConnectionsMap(props.companyId);

            settings.connections[serviceNames.ams] = map[serviceNames.ams] || defaultAmsService();

            settings.connections[serviceNames.jobylon] = map[serviceNames.jobylon] || defaultJobylonService();

            settings.connections[serviceNames.teamtailor] = map[serviceNames.teamtailor] || defaultTeamtailorService();

            settings.connections[serviceNames.teamtailorXml] = map[serviceNames.teamtailorXml] || defaultTeamtailorXmlService();

            settings.connections[serviceNames.teamtailorApi] = map[serviceNames.teamtailorApi] || defaultTeamtailorApiService();

            settings.connections[serviceNames.ponty] = map[serviceNames.ponty] || defaultPontyService();
            settings.connections[serviceNames.intenso] = map[serviceNames.intenso] || defaultIntensoService();

            settings.loaded = true;
        }

        onMounted(async () => {

            await fetchList();
        });

        const connectAms = async () => {

            try {

                settings.loaded = false;

                const data = (await axios.post<Array<{name: string; status: ConnectionStatus; price: number; error?: string}>>(SettingsContext.getApiUrl('connectAms'), {userName: model.ams.username, password: model.ams.password, id: props.companyId})).data;

                try
                {
                    if (data[0]?.error)
                    {
                        states.amsError = data[0].error;
                    }
                }
                catch
                {
                    await generalError(false);
                }

                await fetchList();

            } catch {
                if(settings.connections[serviceNames.ams]) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    settings.connections[serviceNames.ams]!.status = ConnectionStatus.wrongInformation;
                }
                else {
                    settings.connections[serviceNames.ams] = {
                        ...defaultAmsService(),
                        status: ConnectionStatus.wrongInformation
                    };
                }
            }

            settings.loaded = true;
        };

        const connectJobylon = async (connection: JobylonService) => {
            settings.connections[serviceNames.jobylon] = connection ?? defaultJobylonService();
        };

        const connectTeamtailor = async (connection: TeamtailorService) => {
            settings.connections[serviceNames.teamtailor] = connection ?? defaultTeamtailorService();
        };

        const connectTeamtailorXml = async (connection: TeamtailorXmlService) => {
            settings.connections[serviceNames.teamtailorXml] = connection ?? defaultTeamtailorXmlService();
        };

        const connectTeamtailorApi = async (connection: TeamtailorApiServiceInfo) => {
            settings.connections[serviceNames.teamtailorApi] = connection ?? defaultTeamtailorApiService();
        };

        const connectPonty = async (connection: PontyService) => {
            settings.connections[serviceNames.ponty] = connection ?? defaultPontyService();
        };

        const connectIntenso = async (connection: IntensoService) => {
            settings.connections[serviceNames.intenso] = connection ?? defaultIntensoService();
        };

        const pageContext = createPageContext({
            actions: [],
            title: {text: ''},
            breadcrumb: {
                showHome: false,
                segments: [
                    {text: i18n.t('home.breadcrumb-settings') as string, route: `/${props.companyId}/settings`, icon: '<i class="mdi mdi-office-home-cog"></i>'},
                    {text: i18n.t('home.service-connections') as string, icon: '<i class="mdi mdi-lan"></i>' }
                ]
            }
        });

        return {

            serviceNames,

            settings,

            ConnectionStatus,

            model,

            connectAms,

            connectJobylon,

            connectTeamtailor,
            
            connectTeamtailorXml,

            connectTeamtailorApi,

            connectPonty,

            pageContext,

            states,

            teamTailorEnabled,

            teamTailorXmlEnabled,

            shouldDisableTeamtailor,

            pontyEnabled,
            connectIntenso,
        }
    }
});
